import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../components/shared/AuthContext';
import { PageLayout, Table, Button, DatePicker } from "../../components/ui";
import Loader from '../../components/ui/Loader';
import { ReportHeader, ReportSummaryCard } from "../../components/reports";

const fmt = (n) =>
  n != null && n !== "" ? Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "—";

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = d.toString().split("T")[0];
  const [y, m, dd] = dt.split("-");
  return `${dd}-${m}-${y}`;
};

const BUCKET_STYLES = {
  "Current":  { bg: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  "1-30":     { bg: "bg-amber-50 text-amber-700 border-amber-200" },
  "31-60":    { bg: "bg-orange-50 text-orange-700 border-orange-200" },
  "61-90":    { bg: "bg-rose-50 text-rose-700 border-rose-200" },
  "91-180":   { bg: "bg-purple-50 text-purple-700 border-purple-200" },
  "180+":     { bg: "bg-indigo-50 text-indigo-700 border-indigo-200" },
};

const COLUMNS = [
  { key: "sno",                label: "#",                    align: "center" },
  { key: "branchName",         label: "Branch Name",          align: "left"   },
  { key: "branchCode",         label: "Branch Code",          align: "left"   },
  { key: "region",             label: "Region",               align: "left"   },
  { key: "division",           label: "Division",             align: "left"   },
  { key: "customerName",       label: "Customer Name",        align: "left"   },
  { key: "prospectId",         label: "Prospect ID",          align: "left"   },
  { key: "contactNo",          label: "Contact No",           align: "left"   },
  { key: "loanAccountNo",      label: "Loan Account No",      align: "left"   },
  { key: "disbursedAmount",    label: "Disbursed Amount",     align: "right"  },
  { key: "tenureInMonths",     label: "Tenure",               align: "center" },
  { key: "emiAmount",          label: "EMI Amount",           align: "right"  },
  { key: "emiStartDate",       label: "EMI Start Date",       align: "left"   },
  { key: "maturityDate",       label: "Maturity Date",        align: "left"   },
  { key: "emiMonthsPaid",      label: "EMI Months Paid",      align: "center" },
  { key: "overdueEMIs",        label: "Overdue EMIs",         align: "center" },
  { key: "firstOverdueDate",   label: "First Overdue Date",   align: "left"   },
  { key: "lastPaidDate",       label: "Last Paid Date",       align: "left"   },
  { key: "outstandingPrincipal", label: "O/S Principal",      align: "right"  },
  { key: "outstandingInterest",  label: "O/S Interest",       align: "right"  },
  { key: "overduePrincipal",   label: "Overdue Principal",    align: "right"  },
  { key: "overdueInterest",    label: "Overdue Interest",     align: "right"  },
  { key: "totalPAR",           label: "Total PAR",            align: "right"  },
  { key: "closingBalance",     label: "Account Balance",      align: "right"  },
  { key: "principalPaid",      label: "Principal Paid",       align: "right"  },
  { key: "interestPaid",       label: "Interest Paid",        align: "right"  },
  { key: "dpd",                label: "DPD",                  align: "center" },
  { key: "bucket",             label: "Bucket",               align: "center" },
  { key: "employeeName",       label: "Employee Name",        align: "left"   },
  { key: "employeeCode",       label: "Employee Code",        align: "center" },
];

const CURRENCY_KEYS = new Set([
  "disbursedAmount","emiAmount","outstandingPrincipal","outstandingInterest",
  "overduePrincipal","overdueInterest","closingBalance","totalPAR","principalPaid","interestPaid",
]);
const DATE_KEYS = new Set([
  "emiStartDate","maturityDate","firstOverdueDate","lastPaidDate",
]);

const OverdueReport = () => {
  const { data: authData, roleName } = useAuth();
  const branchId    = authData?.branchId || null;
  const isSuperAdmin = roleName === "superadmin";

  const [rows,        setRows]        = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [search,      setSearch]      = useState("");
  const [fromDate,    setFromDate]    = useState("");
  const [toDate,      setToDate]      = useState("");
  const [bucketFilter, setBucketFilter] = useState("All");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (!isSuperAdmin && branchId) params.branchId = branchId;
      if (fromDate) params.fromDate = fromDate;
      if (toDate)   params.toDate   = toDate;

      // 1. Fetch overdue rows
      const res = await axios.get("/api/v2/overdue-report", {
        baseURL: API_BASE_URL,
        params,
      });
      const overdueRows = Array.isArray(res.data?.data) ? res.data.data : [];

      // 2. Fetch member account (savings) closing balances
      if (overdueRows.length > 0) {
        const cuids = overdueRows.map((r) => r.prospectId).filter(Boolean).join(",");
        try {
          const balRes = await axios.post("/api/v2/member-closing-balances", {
            customerUniqueIds: cuids
          }, {
            baseURL: API_BASE_URL
          });
          const balMap = balRes.data?.data || {};
          overdueRows.forEach((r) => {
            r.closingBalance = balMap[r.prospectId] ?? 0;
          });
        } catch (balErr) {
          console.warn("Could not fetch member balances:", balErr);
        }
      }

      setRows(overdueRows);
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
      setError("Failed to load overdue report. Please try again.");
    }
    setLoading(false);
  }, [isSuperAdmin, branchId, fromDate, toDate]);

  useEffect(() => { loadData(); }, [loadData]);

  // Client-side filtering
  const filtered = rows.filter((r) => {
    const q = search.toLowerCase();
    const matchSearch =
      (r.customerName   || "").toLowerCase().includes(q) ||
      (r.loanAccountNo  || "").toLowerCase().includes(q) ||
      (r.prospectId     || "").toLowerCase().includes(q) ||
      (r.branchName     || "").toLowerCase().includes(q) ||
      (r.employeeName   || "").toLowerCase().includes(q) ||
      String(r.contactNo || "").includes(q);
    const matchBucket = bucketFilter === "All" || r.bucket === bucketFilter;
    return matchSearch && matchBucket;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const paginated  = filtered.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  // Summary stats
  const totalPAR       = filtered.reduce((s, r) => s + (Number(r.totalPAR)      || 0), 0);
  const totalOverdueP  = filtered.reduce((s, r) => s + (Number(r.overduePrincipal) || 0), 0);
  const totalOverdueI  = filtered.reduce((s, r) => s + (Number(r.overdueInterest)  || 0), 0);
  const allBucketCounts = { "1-30": 0, "31-60": 0, "61-90": 0, "91-180": 0, "180+": 0 };
  rows.forEach((r) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      (r.customerName   || "").toLowerCase().includes(q) ||
      (r.loanAccountNo  || "").toLowerCase().includes(q) ||
      (r.prospectId     || "").toLowerCase().includes(q) ||
      (r.branchName     || "").toLowerCase().includes(q) ||
      (r.employeeName   || "").toLowerCase().includes(q) ||
      String(r.contactNo || "").includes(q);
    if (matchSearch && allBucketCounts[r.bucket] !== undefined) {
      allBucketCounts[r.bucket]++;
    }
  });

  // CSV Export
  const exportCSV = () => {
    const headers = COLUMNS.map((c) => c.label);
    const csvRows = [
      headers.join(","),
      ...filtered.map((r) =>
        COLUMNS.map((c) => {
          const val = String(r[c.key] ?? "").replace(/"/g, '""');
          return val.includes(",") ? `"${val}"` : val;
        }).join(",")
      ),
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = "overdue_report.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const renderCell = (r, col, idx) => {
    const val = r[col.key];

    if (col.key === "sno") return <span className="text-ink-400">{(currentPage - 1) * rowsPerPage + idx + 1}</span>;

    if (DATE_KEYS.has(col.key)) {
      const txt = fmtDate(val);
      const isFirstOverdue = col.key === "firstOverdueDate" && val;
      return <span className={isFirstOverdue ? "text-rose-600 font-bold" : ""}>{txt}</span>;
    }

    if (CURRENCY_KEYS.has(col.key)) {
      if (val === "" || val == null) return "—";
      const isOverdue = ["overduePrincipal","overdueInterest","totalPAR"].includes(col.key);
      const isClosing = col.key === "closingBalance";
      return (
        <span className={isOverdue ? "text-rose-600 font-bold" : isClosing ? "text-[#0369a1] font-bold" : ""}>
          ₹{fmt(val)}
        </span>
      );
    }

    if (col.key === "dpd") {
      const colorClass = val > 90 ? "text-purple-600 font-bold" :
                         val > 60 ? "text-rose-600 font-bold" :
                         val > 30 ? "text-orange-600 font-bold" :
                         val > 0 ? "text-amber-600 font-bold" : "text-emerald-600 font-bold";
      return <span className={colorClass}>{val}</span>;
    }

    if (col.key === "bucket") {
      const style = BUCKET_STYLES[val] || { bg: "bg-ink-50 text-ink-700 border-ink-200" };
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${style.bg}`}>
          {val}
        </span>
      );
    }

    if (col.key === "customerName") return <span className="font-semibold text-ink-900">{val || "—"}</span>;
    if (col.key === "loanAccountNo") return <code className="text-xs font-mono text-primary font-bold">{val || "—"}</code>;
    if (col.key === "overdueEMIs") {
      return <span className="text-rose-600 font-bold">{val}</span>;
    }

    return val !== "" && val != null ? val : "—";
  };

  return (
    <PageLayout className="h-auto min-h-[calc(100vh-120px)] overflow-y-auto pb-4">
      {/* Top Filter Bar using reusable ReportHeader */}
      <ReportHeader
        filters={
          <div className="flex flex-row flex-wrap items-center gap-2">
            {/* From Date */}
            <div className="w-[130px] mt-1">
              <DatePicker
                label="From Date"
                selected={fromDate}
                onChange={(date) => setFromDate(date ? date.toISOString().split('T')[0] : '')}
                placeholder="YYYY-MM-DD"
              />
            </div>

            {/* To Date */}
            <div className="w-[130px] mt-1">
              <DatePicker
                label="To Date"
                selected={toDate}
                onChange={(date) => setToDate(date ? date.toISOString().split('T')[0] : '')}
                placeholder="YYYY-MM-DD"
              />
            </div>

            {/* DPD Dropdown */}
            <div className="relative w-[125px] mt-1">
              <label className="absolute -top-2 left-2.5 bg-white px-1 text-[11px] font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                DPD
              </label>
              <div className="relative">
                <select
                  value={bucketFilter}
                  onChange={(e) => { setBucketFilter(e.target.value); setCurrentPage(1); }}
                  className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-2.5 pr-6 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium"
                >
                  <option value="All">All DPD</option>
                  <option value="1-30">1-30 DPD ({allBucketCounts["1-30"] || 0})</option>
                  <option value="31-60">31-60 DPD ({allBucketCounts["31-60"] || 0})</option>
                  <option value="61-90">61-90 DPD ({allBucketCounts["61-90"] || 0})</option>
                  <option value="91-180">91-180 DPD ({allBucketCounts["91-180"] || 0})</option>
                  <option value="180+">180+ DPD ({allBucketCounts["180+"] || 0})</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-ink-400">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Rows/Page */}
            <div className="relative w-[90px] mt-1">
              <label className="absolute -top-2 left-2.5 bg-white px-1 text-[11px] font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                Rows/Page
              </label>
              <div className="relative">
                <select
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-2.5 pr-6 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium"
                >
                  {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-ink-400">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Apply Button */}
            <div className="mt-1">
              <Button onClick={loadData} variant="secondary" className="h-[38px] px-2.5 font-semibold text-xs whitespace-nowrap">
                Apply Filters
              </Button>
            </div>

            {/* Search Input */}
            <div className="w-[170px] mt-1">
              <input
                placeholder="Search..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                className="w-full bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary text-ink-700 h-[38px] text-xs px-2.5 focus:outline-none transition-all font-medium"
              />
            </div>
          </div>
        }
        actions={
          <Button
            onClick={exportCSV}
            variant="primary"
            size="sm"
            className="shadow-sm h-[38px] px-3.5 font-bold whitespace-nowrap"
          >
            <i className="fa-solid fa-file-csv mr-1.5"></i>
            Export CSV
          </Button>
        }
      />
      {loading && !rows.length && <Loader message="Loading overdue data..." />}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        {[
          {
            label: "Overdue Accounts",
            value: filtered.length,
            bg: "bg-[#E9DFFF]",
            border: "border-[#7C3AED]/25",
            accent: "text-[#7C3AED]",
            iconBg: "bg-white/80",
            icon: "fa-users",
          },
          {
            label: "Total PAR",
            value: `₹${fmt(totalPAR)}`,
            bg: "bg-[#FFF0C7]",
            border: "border-[#D97706]/25",
            accent: "text-[#D97706]",
            iconBg: "bg-white/80",
            icon: "fa-clock",
          },
          {
            label: "Overdue Principal",
            value: `₹${fmt(totalOverdueP)}`,
            bg: "bg-[#D9F3F7]",
            border: "border-[#0891B2]/25",
            accent: "text-[#0891B2]",
            iconBg: "bg-white/80",
            icon: "fa-money-bill-wave",
          },
          {
            label: "Overdue Interest",
            value: `₹${fmt(totalOverdueI)}`,
            bg: "bg-[#DCEBFF]",
            border: "border-[#2563EB]/25",
            accent: "text-[#2563EB]",
            iconBg: "bg-white/80",
            icon: "fa-percent",
          },
        ].map((c, idx) => (
          <ReportSummaryCard
            key={idx}
            label={c.label}
            value={c.value}
            bg={c.bg}
            border={c.border}
            accent={c.accent}
            iconBg={c.iconBg}
            icon={c.icon}
          />
        ))}
      </div>

      {error && (
        <div className="bg-rose-50 text-rose-700 p-4 rounded-xl border border-rose-200 text-sm font-medium mb-4">
          {error}
        </div>
      )}

      {/* Main Table */}
      <div className="flex-grow flex flex-col min-h-0">
        <Table className="min-w-[2800px]">
          <Table.Head>
            <tr>
              {COLUMNS.map((c) => (
                <Table.HeadCell key={c.key} align={c.align} className="whitespace-nowrap">
                  {c.label}
                </Table.HeadCell>
              ))}
            </tr>
          </Table.Head>
          <Table.Body>
            {loading ? (
              <Table.Row>
                <Table.Cell colSpan={COLUMNS.length} align="center" className="py-10">
                  <div className="flex flex-col items-center justify-center py-4">
                    <Loader size="md" />
                  </div>
                </Table.Cell>
              </Table.Row>
            ) : paginated.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={COLUMNS.length} align="center" className="py-12">
                  <div className="flex flex-col items-center justify-center gap-2 text-emerald-600">
                    <i className="fa-solid fa-circle-check text-3xl"></i>
                    <span className="font-bold text-sm">No overdue records found. All accounts are up-to-date!</span>
                  </div>
                </Table.Cell>
              </Table.Row>
            ) : (
              paginated.map((r, i) => {
                const isOdd = i % 2 !== 0;
                return (
                  <Table.Row key={i} className={isOdd ? "bg-rose-50/5" : ""}>
                    {COLUMNS.map((col) => (
                      <Table.Cell key={col.key} align={col.align} className="whitespace-nowrap">
                        {renderCell(r, col, i)}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                );
              })
            )}
          </Table.Body>
        </Table>

        {/* Pagination Controls */}
        {filtered.length > 0 && (
          <div className="flex items-center justify-between mt-4 bg-white p-3 rounded-xl border border-border-light shadow-sm">
            <span className="text-xs text-ink-500">
              Showing {paginated.length} of {filtered.length} records {filtered.length !== rows.length && `(filtered from ${rows.length})`}
            </span>
            <div className="flex gap-1 items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                First
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className="text-xs text-ink-500 px-3 font-semibold">
                Page {currentPage} / {totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                Last
              </Button>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default OverdueReport;
